import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

export default function TokenModal({ open, onClose, tokens, onSelect, chainName }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (!open) setQ(""); }, [open]);

  const filtered = useMemo(() => {
    if (!tokens) return [];
    const s = q.trim().toLowerCase();
    const list = s
      ? tokens.filter(
          (t) =>
            t.symbol?.toLowerCase().includes(s) ||
            t.name?.toLowerCase().includes(s) ||
            t.address?.toLowerCase().includes(s)
        )
      : tokens;
    return list.slice(0, 200);
  }, [q, tokens]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.35)] backdrop-blur-md" />
      <div className="relative panel w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 h-12 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[13px] text-[var(--text)] font-semibold">Select token</div>
            <div className="text-[11px] text-[var(--text-muted)] font-mono">on {chainName}</div>
          </div>
          <button
            className="w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] flex items-center justify-center"
            onClick={onClose}
            data-testid="token-modal-close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-3">
          <div className="input flex items-center gap-2 h-10">
            <Search size={14} className="text-[var(--text-muted)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, symbol or paste address"
              className="bg-transparent outline-none text-[13px] text-[var(--text)] flex-1 placeholder:text-[var(--text-subtle)]"
              data-testid="token-search-input"
            />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto pb-2">
          {!tokens && <div className="px-4 py-4 text-[13px] text-[var(--text-muted)]">Loading tokens…</div>}
          {tokens && filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-[var(--text-subtle)]">No tokens found</div>
          )}
          {filtered.map((t) => (
            <button
              key={`${t.chainId}-${t.address}`}
              onClick={() => { onSelect(t); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--panel-2)] transition-colors text-left"
              data-testid={`token-option-${t.symbol}`}
            >
              {t.logoURI ? (
                <img src={t.logoURI} alt="" className="w-7 h-7 rounded-full bg-[var(--panel-3)]" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[var(--panel-3)] flex items-center justify-center text-[11px] text-[var(--text)]">
                  {t.symbol?.[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[var(--text)] font-semibold">{t.symbol}</div>
                <div className="text-[11px] text-[var(--text-muted)] truncate">{t.name}</div>
              </div>
              <div className="text-[10px] text-[var(--text-subtle)] font-mono">
                {t.address?.slice(0, 6)}…{t.address?.slice(-4)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
